import { useQuery } from '@tanstack/react-query';

import { apiService } from '../../services/api.service';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';

/**
 * Are native spreadsheets available to the current WORKSPACE? Yes everywhere
 * except a PHI workspace; the server decides (`native-spreadsheets-gate.ts`),
 * because only it can read the workspace's PHI status. The rollout flag this
 * used to read was retired on 2026-10-14; the route keeps its old path.
 *
 * Keyed by workspace so a switch never serves another workspace's answer. An
 * unknown answer throws, so it is retried rather than cached as "off"; the
 * hook still fails closed.
 */
export const spreadsheetsEnabledKey = (workspaceId: string | null | undefined) =>
  ['spreadsheets-enabled', { workspaceId: workspaceId ?? null }] as const;

export function useSpreadsheetsEnabled(): boolean {
  const workspaceId = useWorkspaceId();
  const { data } = useQuery({
    queryKey: spreadsheetsEnabledKey(workspaceId),
    queryFn: async () => {
      const response = await apiService.get<{ enabled: boolean; known?: boolean }>(
        '/spreadsheets/feature-flag',
      );
      if (!response.success || response.data?.known === false) {
        throw new Error('spreadsheet availability unknown');
      }
      return response.data?.enabled === true;
    },
    enabled: Boolean(workspaceId),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt) => 400 * 2 ** attempt,
  });
  return data ?? false;
}
